// prefetch/api.ts
import axios from 'axios';
import rpcLeads from '../api/rpc/rpcLeads';
import { getAdminClients, getAdminAgentsClients } from '../api/clientsApi';
import { prefetchJSON, ensureJSON, getPrefetched, clearCache } from './cache';

const baseUrl = process.env.REACT_APP_API_URL;

/* =========================
   Lightweight Auth Snapshot
   ========================= */
// Keep the current user's role/permissions in-memory.
// Set this once after login or on auth refresh.
type AuthSnapshot = { role?: string; permissions: string[] };
let auth: AuthSnapshot = { role: undefined, permissions: [] };

export function setAuthSnapshot(next: Partial<AuthSnapshot>) {
  auth = {
    role: next.role ?? auth.role,
    permissions: Array.isArray(next.permissions) ? next.permissions : auth.permissions,
  };
}
export function clearAuthSnapshot() {
  auth = { role: undefined, permissions: [] };
}

/* =========================
   Permission Helpers
   ========================= */
const isAdmin = () => auth.role === 'admin';
const hasPermission = (key: string) => auth.permissions.includes(key); // 👈 your check

const canOverview             = () => isAdmin() || hasPermission('overview');
const canViewClients          = () => isAdmin() || hasPermission('view_clients');
const canViewAgents           = () => isAdmin() || hasPermission('view_agent');
const canViewPotentialClients = () => isAdmin() || hasPermission('view_potential_clients');
const canViewTasks            = () => isAdmin() || hasPermission('view_tasks');
const canViewLeads            = () => isAdmin() || hasPermission('view_leads');
const canViewSlots            = () => isAdmin() || hasPermission('view_slots');
const canViewManagers         = () => isAdmin() || hasPermission('view_manager');
const isManager               = () => auth.role === 'manager';

/* =========================
   Types & Param Normalizer
   ========================= */
type PageParams = { page?: number; pageSize?: number };
type AnyParams = Record<string, any> & PageParams;
type PageArg = number | AnyParams;

function normalizeParams(arg?: PageArg): AnyParams {
  if (arg == null) return {};
  if (typeof arg === 'number') return { page: arg };
  return arg;
}

/* ==========  CACHE KEYS  ========== */
export const keys = {
  dashboard: (company: string) => ['dashboard', company].join(':'),
  clients: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['clients', company, JSON.stringify(p)].join(':');
  },
  agents: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['agents', company, JSON.stringify(p)].join(':');
  },
  potentialClients: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['potentialClients', company, JSON.stringify(p)].join(':');
  },
  managerTasks: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['managerTasks', company, JSON.stringify(p)].join(':');
  },
  managerLeads: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['managerLeads', company, JSON.stringify(p)].join(':');
  },
  managers: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['managers', company, JSON.stringify(p)].join(':');
  },
  slots: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['allSlots', company, JSON.stringify(p)].join(':');
  },
  adminTasks: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['adminTasks', company, JSON.stringify(p)].join(':');
  },
  adminLeads: (company: string, params?: PageArg) => {
    const p = normalizeParams(params);
    return ['adminLeads', company, JSON.stringify(p)].join(':');
  },
};

/* ==========  raw FETCHERS  ========== */
const fx = {
  dashboard: (company: string) => async () => {
    const rpcSettings = (await import('../api/rpc/settings')).default;
    const res = await rpcSettings.getAdminOverview();
    if (res.data.ok) {
      return res.data.data;
    }
    return undefined;
  },

  clients: (company: string, params?: PageArg) => () => {
    const p = normalizeParams(params);
    return getAdminClients(company, p);
  },

  agents: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const rpcAgents = (await import('../api/rpc/rpcAgents')).default;
    const res = await rpcAgents.listAdmin();
    if (res.data.ok) {
      return res.data.data;
    }
    return [];
  },

  // GET /api/:company/admin/agents/clients?agentId=&page=&pageSize=
  potentialClients: (company: string, params?: PageArg) => () => {
    const p = normalizeParams(params);
    return getAdminAgentsClients(company, p);
  },

  // Manager Tasks
  managerTasks: (company: string, params?: PageArg) => async () => {
    const rpcManagers = (await import('../api/rpc/rpcManagers')).default;
    const p = normalizeParams(params);
    const { data } = await rpcManagers.listTasks({
      status: p.status as string | undefined,
      month: p.month as string | undefined,
      clientId: p.clientId as string | undefined,
      q: p.q as string | undefined,
    });
    if (data.ok) {
      return data.data;
    }
    throw new Error(data.error || 'Failed to fetch manager tasks');
  },

  // Manager Leads
  managerLeads: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const res = await rpcLeads.list(p);
    if (res.data.ok) {
      return res.data.data;
    }
    return { items: [], total: 0, page: 1, pages: 1 };
  },

  // Managers
  managers: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const rpcManagers = (await import('../api/rpc/rpcManagers')).default;
    const res = await rpcManagers.list();
    if (res.data.ok) {
      return res.data.data;
    }
    return [];
  },

  // Admin Tasks
  adminTasks: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const rpcAdminTasks = (await import('../api/rpc/rpcAdminTasks')).default;
    const res = await rpcAdminTasks.list(p);
    if (res.data.ok) {
      return res.data.data;
    }
    return [];
  },

  // Admin Leads
  adminLeads: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const res = await rpcLeads.list(p);
    if (res.data.ok) {
      return res.data.data;
    }
    return { items: [], total: 0, page: 1, pages: 1 };
  },

  // Slots
  slots: (company: string, params?: PageArg) => async () => {
    const p = normalizeParams(params);
    const rpcSlots = (await import('../api/rpc/rpcSlots')).default;
    const res = await rpcSlots.list();
    if (res.data.ok) {
      return res.data.data;
    }
    return [];
  },
};

/* ==========  safety wrapper: never throw  ========== */
const nonThrowing =
  <T,>(loader: () => Promise<T>) =>
  async (): Promise<T | undefined> => {
    try {
      return await loader();
    } catch {
      return undefined;
    }
  };

/* ==========  PREFETCHERS (permission-aware)  ========== */
export const prefetchDashboard = (company?: string) => {
  if (!company || !canOverview()) return;
  return prefetchJSON(keys.dashboard(company), nonThrowing(fx.dashboard(company)));
};

export const prefetchClients = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewClients()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.clients(company, p), nonThrowing(fx.clients(company, p)));
};

/** Warm the Agents page with whatever the user is allowed to see */
export const prefetchAgentsAny = (company?: string, pageOrParams?: PageArg) => {
  if (!company) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});

  if (canViewAgents()) {
    return prefetchJSON(keys.agents(company, p), nonThrowing(fx.agents(company, p)));
  }
  if (canViewPotentialClients()) {
    return prefetchJSON(keys.potentialClients(company, p), nonThrowing(fx.potentialClients(company, p)));
  }
  // no permission → no request
  return;
};

export const prefetchAgents = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewAgents()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.agents(company, p), nonThrowing(fx.agents(company, p)));
};

export const prefetchPotentialClients = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewPotentialClients()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.potentialClients(company, p), nonThrowing(fx.potentialClients(company, p)));
};

export const prefetchManagerTasks = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewTasks()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.managerTasks(company, p), nonThrowing(fx.managerTasks(company, p)));
};

export const prefetchManagerLeads = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewLeads()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.managerLeads(company, p), nonThrowing(fx.managerLeads(company, p)));
};

export const prefetchManagers = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewManagers()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.managers(company, p), nonThrowing(fx.managers(company, p)));
};

export const prefetchAdminTasks = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !isAdmin()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.adminTasks(company, p), nonThrowing(fx.adminTasks(company, p)));
};

export const prefetchAdminLeads = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !isAdmin()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.adminLeads(company, p), nonThrowing(fx.adminLeads(company, p)));
};

export const prefetchSlots = (company?: string, pageOrParams?: PageArg) => {
  if (!company || !canViewSlots()) return;
  const p = typeof pageOrParams === 'number' ? { page: pageOrParams } : (pageOrParams ?? {});
  return prefetchJSON(keys.slots(company, p), nonThrowing(fx.slots(company, p)));
};

/**
 * Prefetch Contracts using React Query
 * This uses React Query's prefetchQuery (not the old prefetchJSON cache)
 * to ensure data is warmed in React Query cache before navigation
 */
export const prefetchContracts = async (company?: string, params?: { page?: number; limit?: number; search?: string; status?: string; includeArchived?: boolean }) => {
  if (!company) return;
  
  // Import React Query and contracts API dynamically to avoid circular deps
  const { queryClient } = await import('../lib/queryClient');
  const { contractKeys } = await import('../lib/queryKeys');
  const { getContracts } = await import('../api/contractsApi');
  const { queryOptions } = await import('../lib/queryClient');
  
  const defaultParams = {
    page: 1,
    limit: 25,
    ...params,
  };
  
  const queryKey = contractKeys.list(company, defaultParams);
  
  // Prefetch using React Query with heavyTable options
  return queryClient.prefetchQuery({
    queryKey,
    queryFn: async () => {
      const response = await getContracts(company, defaultParams);
      
      // Normalize response format to match useContractsQuery
      if (response.contracts && Array.isArray(response.contracts)) {
        return {
          contracts: response.contracts,
          pagination: response.pagination,
          cursor: response.cursor,
        };
      }
      
      if (Array.isArray(response)) {
        return {
          contracts: response,
          pagination: {
            page: 1,
            limit: response.length,
            total: response.length,
            pages: 1,
            hasMore: false,
          },
        };
      }
      
      return {
        contracts: [],
        pagination: {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0,
          hasMore: false,
        },
      };
    },
    ...queryOptions.heavyTable,
  });
};

/**
 * Prefetch Deleted Items using React Query
 * This uses React Query's prefetchQuery to warm the cache before navigating to settings?tab=deleted
 */
export const prefetchDeletedItems = async (company?: string, params?: { page?: number; limit?: number; search?: string; coll?: string }) => {
  if (!company) return;
  
  // Only admins/managers can access deleted items
  if (!isAdmin() && !isManager()) return;
  
  // Import React Query and deleted items API dynamically to avoid circular deps
  const { queryClient } = await import('../lib/queryClient');
  const { deletedItemsKeys } = await import('../lib/queryKeys');
  const { getBackups } = await import('../api/backupsApi');
  const { queryOptions } = await import('../lib/queryClient');
  
  const defaultParams = {
    page: 1,
    limit: 25,
    search: '',
    ...params,
  };
  
  const queryKey = deletedItemsKeys.list(company, defaultParams);
  
  // Prefetch using React Query with heavyTable options
  return queryClient.prefetchQuery({
    queryKey,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(defaultParams.page));
      queryParams.set('limit', String(defaultParams.limit));
      queryParams.set('op', 'delete');
      
      if (defaultParams.search) {
        queryParams.set('search', defaultParams.search);
      }
      if (defaultParams.coll && defaultParams.coll !== 'all') {
        queryParams.set('coll', defaultParams.coll);
      }
      
      const response = await getBackups(company, queryParams);
      
      // Normalize response format to match useDeletedItemsQuery
      if (response && typeof response === 'object' && 'items' in response && 'pagination' in response) {
        return {
          items: response.items || [],
          pagination: response.pagination,
        };
      }
      
      if (Array.isArray(response)) {
        return {
          items: response,
          pagination: {
            page: 1,
            limit: response.length,
            total: response.length,
            pages: 1,
            hasMore: false,
          },
        };
      }
      
      return {
        items: [],
        pagination: {
          page: 1,
          limit: 25,
          total: 0,
          pages: 0,
          hasMore: false,
        },
      };
    },
    ...queryOptions.heavyTable,
  });
};

/* ==========  READ / LOAD (permission-aware)  ========== */
export const readDashboard = (company: string) =>
  getPrefetched(keys.dashboard(company));

export const loadDashboard = (company: string) => {
  if (!canOverview()) return Promise.resolve(undefined);
  return ensureJSON(keys.dashboard(company), nonThrowing(fx.dashboard(company)));
};

export const readClients = (company: string, params?: PageArg) =>
  getPrefetched(keys.clients(company, params));

export const loadClients = (company: string, params?: PageArg) => {
  if (!canViewClients()) return Promise.resolve(undefined);
  return ensureJSON(keys.clients(company, params), nonThrowing(fx.clients(company, params)));
};

export const readAgents = (company: string, params?: PageArg) =>
  getPrefetched(keys.agents(company, params));

export const loadAgents = (company: string, params?: PageArg) => {
  if (!canViewAgents()) return Promise.resolve(undefined);
  return ensureJSON(keys.agents(company, params), nonThrowing(fx.agents(company, params)));
};

export const readPotentialClients = (company: string, params?: PageArg) =>
  getPrefetched(keys.potentialClients(company, params));

export const loadPotentialClients = (company: string, params?: PageArg) => {
  if (!canViewPotentialClients()) return Promise.resolve(undefined);
  return ensureJSON(keys.potentialClients(company, params), nonThrowing(fx.potentialClients(company, params)));
};

/** One adaptive loader to use inside Agents page */
export const loadAgentsAny = (company: string, params?: PageArg) => {
  if (canViewAgents()) {
    return ensureJSON(keys.agents(company, params), nonThrowing(fx.agents(company, params)));
  }
  if (canViewPotentialClients()) {
    return ensureJSON(keys.potentialClients(company, params), nonThrowing(fx.potentialClients(company, params)));
  }
  return Promise.resolve(undefined);
};

// Manager Tasks
export const readManagerTasks = (company: string, params?: PageArg) =>
  getPrefetched(keys.managerTasks(company, params));

export const loadManagerTasks = (company: string, params?: PageArg) => {
  if (!canViewTasks()) return Promise.resolve(undefined);
  return ensureJSON(keys.managerTasks(company, params), nonThrowing(fx.managerTasks(company, params)));
};

// Manager Leads
export const readManagerLeads = (company: string, params?: PageArg) =>
  getPrefetched(keys.managerLeads(company, params));

export const loadManagerLeads = (company: string, params?: PageArg) => {
  if (!canViewLeads()) return Promise.resolve(undefined);
  return ensureJSON(keys.managerLeads(company, params), nonThrowing(fx.managerLeads(company, params)));
};

// Managers
export const readManagers = (company: string, params?: PageArg) =>
  getPrefetched(keys.managers(company, params));

export const loadManagers = (company: string, params?: PageArg) => {
  if (!canViewManagers()) return Promise.resolve(undefined);
  return ensureJSON(keys.managers(company, params), nonThrowing(fx.managers(company, params)));
};

// Admin Tasks
export const readAdminTasks = (company: string, params?: PageArg) =>
  getPrefetched(keys.adminTasks(company, params));

export const loadAdminTasks = (company: string, params?: PageArg) => {
  if (!isAdmin()) return Promise.resolve(undefined);
  return ensureJSON(keys.adminTasks(company, params), nonThrowing(fx.adminTasks(company, params)));
};

// Admin Leads
export const readAdminLeads = (company: string, params?: PageArg) =>
  getPrefetched(keys.adminLeads(company, params));

export const loadAdminLeads = (company: string, params?: PageArg) => {
  if (!isAdmin()) return Promise.resolve(undefined);
  return ensureJSON(keys.adminLeads(company, params), nonThrowing(fx.adminLeads(company, params)));
};

// Slots
export const readSlots = (company: string, params?: PageArg) =>
  getPrefetched(keys.slots(company, params));

export const loadSlots = (company: string, params?: PageArg) => {
  if (!canViewSlots()) return Promise.resolve(undefined);
  return ensureJSON(keys.slots(company, params), nonThrowing(fx.slots(company, params)));
};

/* ==========  CACHE MANAGEMENT  ========== */
export const clearManagerTasksCache = (company: string, params?: PageArg) => {
  clearCache(keys.managerTasks(company, params));
};

export const clearManagerLeadsCache = (company: string, params?: PageArg) => {
  clearCache(keys.managerLeads(company, params));
};

export const clearManagersCache = (company: string, params?: PageArg) => {
  clearCache(keys.managers(company, params));
};

export const clearAdminTasksCache = (company: string, params?: PageArg) => {
  clearCache(keys.adminTasks(company, params));
};

export const clearAdminLeadsCache = (company: string, params?: PageArg) => {
  clearCache(keys.adminLeads(company, params));
};

export const clearSlotsCache = (company: string, params?: PageArg) => {
  clearCache(keys.slots(company, params));
};
